/**
 * ClutchTimeSummary.js
 *
 * Session summary component shown after completing a Clutch Time session.
 * Shows progress gains and encouragement.
 *
 * MVP Implementation - December 2025
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

// Progress delta display
const TopicProgress = ({ topic, index }) => {
  const isPositive = topic.delta > 0;
  const percentage = Math.min(100, Math.max(0, topic.mastery_after));

  return (
    <motion.div
      className="clutch-summary-topic"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <div className="clutch-summary-topic-header">
        <span className="clutch-summary-topic-name">{topic.name}</span>
        <span className="clutch-summary-delta">
          <span className="clutch-summary-before">{Math.round(topic.mastery_before)}%</span>
          <span className="clutch-summary-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </span>
          <span className="clutch-summary-after">{Math.round(topic.mastery_after)}%</span>
          {isPositive && (
            <span className="clutch-summary-gain">+{Math.round(topic.delta)}%</span>
          )}
        </span>
      </div>
      <div className="clutch-summary-bar-bg">
        <motion.div
          className="clutch-summary-bar-fill"
          initial={{ width: `${topic.mastery_before}%` }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8, delay: index * 0.1 + 0.2 }}
        />
      </div>
    </motion.div>
  );
};

const ClutchTimeSummary = ({
  sessionId,
  onDone,
  onKeepStudying
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  // Fetch summary on mount
  useEffect(() => {
    const fetchSummary = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // End the session and get summary
        const response = await axios.post(`/api/clutch-time/session/${sessionId}/end`, {}, {
          withCredentials: true
        });

        if (response.data.success) {
          setSummary(response.data.summary);
        } else {
          setError(response.data.error || 'Failed to get summary');
        }
      } catch (err) {
        console.error('Error fetching summary:', err);
        setError(err.response?.data?.error || 'Failed to get summary');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [sessionId]);

  // Format duration
  const formatDuration = (minutes) => {
    if (minutes < 1) return 'Less than a minute';
    if (minutes === 1) return '1 minute';
    return `${minutes} minutes`;
  };

  return (
    <div className="clutch-summary-container">
      {/* Header */}
      <div className="clutch-summary-header">
        <div className="clutch-summary-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
          </svg>
        </div>
        <div className="clutch-summary-title">
          <h2>Session Complete</h2>
          {summary && (
            <span className="clutch-summary-duration">
              {formatDuration(summary.duration_minutes)}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="clutch-summary-content">
        {loading && (
          <div className="clutch-loading">
            <div className="clutch-spinner" />
            <p>Calculating your progress...</p>
          </div>
        )}

        {error && (
          <div className="clutch-error">
            <p>{error}</p>
            <button onClick={onDone}>Close</button>
          </div>
        )}

        {!loading && !error && summary && (
          <AnimatePresence>
            {/* Progress section */}
            {summary.topics_studied.length > 0 ? (
              <motion.div
                className="clutch-summary-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h3 className="clutch-summary-section-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                    <polyline points="17 6 23 6 23 12"/>
                  </svg>
                  YOUR PROGRESS
                </h3>

                <div className="clutch-summary-topics">
                  {summary.topics_studied.map((topic, idx) => (
                    <TopicProgress key={idx} topic={topic} index={idx} />
                  ))}
                </div>

                {summary.total_delta > 0 && (
                  <motion.div
                    className="clutch-summary-total"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <span className="clutch-summary-total-label">Total gain:</span>
                    <span className="clutch-summary-total-value">+{Math.round(summary.total_delta)}%</span>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <motion.div
                className="clutch-summary-empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <p>Keep going! More practice will show progress.</p>
              </motion.div>
            )}

            {/* Encouragement message */}
            <motion.div
              className="clutch-summary-encouragement"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <p>{summary.encouragement}</p>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Footer actions */}
      {!loading && !error && (
        <div className="clutch-summary-footer">
          <button
            className="clutch-done-btn"
            onClick={onDone}
          >
            Done
          </button>
          <button
            className="clutch-continue-btn"
            onClick={onKeepStudying}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            Keep Studying
          </button>
        </div>
      )}
    </div>
  );
};

export default ClutchTimeSummary;
